import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { downloadSkillBundle } from "@/api/code-companion-access";
import { Button } from "@/components/ui/button";
import { useCcIssueMyGrant } from "@/hooks/useCodeCompanionAccess";
import { resolveMcpUrl } from "@/lib/mcp-url";

const MCP_URL = resolveMcpUrl();
const mcpCmd = (tok: string) =>
  `claude mcp add --transport http mygrow ${MCP_URL} \\\n` +
  `  --header "Authorization: Bearer ${tok}"`;

function CodeLine({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="mt-3 flex items-start gap-2 rounded-lg p-3"
      style={{ background: "hsl(var(--primary))" }}
    >
      <pre className="flex-1 whitespace-pre-wrap break-all font-mono text-xs text-white">
        {text}
      </pre>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          navigator.clipboard?.writeText(text);
          toast.success(t("Copied"));
        }}
      >
        {t("Copy")}
      </Button>
    </div>
  );
}

function Step({
  n,
  title,
  desc,
  children,
}: {
  n: number;
  title: string;
  desc: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex gap-3">
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold"
          style={{ background: "hsl(var(--accent) / 0.2)", color: "hsl(var(--primary))" }}
        >
          {n}
        </div>
        <div className="flex-1">
          <div className="font-brand text-sm font-semibold text-primary">{title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Prompts de exemplo, na ordem do fluxo real.
 *
 * Os dois últimos eram do modelo anterior ao modo Assistido: mandavam o dev construir
 * arquivos num repositório e depois avisar onde eles estavam. Desde a CC.17/CC.26 o
 * código é criado e **ativado no SAP alvo** pelo ADT MCP, e o myGROW é o control plane
 * que valida antes e registra cada fase. Um dev novo seguindo os prompts antigos passava
 * a primeira hora construindo a coisa certa no lugar errado.
 */
const PROMPTS: { title: string; text: string }[] = [
  {
    title: "List and claim",
    text: "List the migration packages available to me, then claim the one I should work on.",
  },
  {
    title: "Pull context and legacy code",
    text: "Pull the context and the legacy code for my package, and summarize what needs to change.",
  },
  {
    title: "Check the target is bound",
    text: "Check the execution context for this package — is a GROW target bound, and which release?",
  },
  {
    title: "Migrate with tests",
    text: "Migrate the first in-scope object following the package CLAUDE.md, with unit tests.",
  },
  {
    title: "Preflight before writing",
    text: "Collect the target state for the change set objects and run the deploy preflight — do not write anything if it is not ready.",
  },
  {
    title: "Write and activate on the target",
    text: "Preflight passed: create and activate these objects on the GROW DEV target in build order, and record each phase.",
  },
  {
    title: "Run the quality gates",
    text: "Run syntax, ATC and ABAP Unit on what we just activated, and show me what failed.",
  },
  {
    title: "Flag incomplete context",
    text: "The context is missing the DDIC table fields for this object — flag it and don't guess the fields.",
  },
  {
    title: "Report a blocker",
    text: "I'm blocked: this depends on a function module that hasn't been released yet.",
  },
  {
    title: "Mark ready for deploy",
    text: "Every in-scope object is activated on the target and green on all gates — mark the package ready for deploy.",
  },
];

const SAP_ADT_CMD =
  "claude mcp add --transport http sap-adt http://localhost:2234/mcp \\\n" +
  '  --header "Authorization: Bearer <your-adt-token>"';

const SKILL_URL = `${window.location.origin}/api/code-companion/skill-bundle`;
const skillSh = (tok: string) =>
  `curl -fsSL -H "Authorization: Bearer ${tok}" ${SKILL_URL} -o cc-skill.zip \\\n` +
  "  && mkdir -p .claude/skills && unzip -o cc-skill.zip -d .claude/skills && rm cc-skill.zip";
const skillPs = (tok: string) =>
  `Invoke-WebRequest -Headers @{Authorization="Bearer ${tok}"} ${SKILL_URL} -OutFile cc-skill.zip; ` +
  "New-Item -ItemType Directory -Force .claude\\skills | Out-Null; " +
  "Expand-Archive -Force cc-skill.zip .claude\\skills; Remove-Item cc-skill.zip";

export function SetupScreen() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [downloading, setDownloading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const issue = useCcIssueMyGrant(projectId ?? "");
  const tok = token ?? "<your-token>";

  function genToken() {
    issue.mutate(undefined, {
      onSuccess: (g) => {
        setToken(g.token);
        toast.success(t("Token ready — the commands below are filled in"));
      },
      onError: () =>
        toast.error(t("Could not generate your token. Ask the owner to link you to this project.")),
    });
  }

  async function getSkill() {
    setDownloading(true);
    try {
      const blob = await downloadSkillBundle();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "code-companion-skill.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Could not download the skill bundle"));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-3">
      <div>
        <h1 className="font-brand text-xl font-semibold text-primary">
          {t("Developer Setup")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "Connect your Claude to this project. Pull package context, build the solution with tests, report status by chatting.",
          )}
        </p>
      </div>

      <Step
        n={1}
        title={t("1 · Install Claude Code")}
        desc={t("Requires Node 18+. Run in your terminal, then authenticate.")}
      >
        <CodeLine text="npm install -g @anthropic-ai/claude-code" />
      </Step>

      <Step
        n={2}
        title={t("2 · Add the myGROW MCP connector")}
        desc={t(
          "Wires your Claude to Code Companion. Generate your token to fill in the commands below (keep it private).",
        )}
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={token ? "outline" : "default"}
            disabled={issue.isPending}
            onClick={genToken}
          >
            {issue.isPending
              ? t("Generating…")
              : token
                ? t("Regenerate my token")
                : t("Generate my access token")}
          </Button>
          {token && (
            <span className="text-[11px]" style={{ color: "hsl(var(--warning))" }}>
              {t(
                "Replaces your previous tokens for this project. If your Claude was already connected, run `claude mcp remove mygrow` and add it again with this command.",
              )}
            </span>
          )}
        </div>
        <CodeLine text={mcpCmd(tok)} />
      </Step>

      <Step
        n={3}
        title={t("3 · Install the Code Companion skill")}
        desc={t(
          "This skill teaches your Claude the migration flow and how to report status by chatting. Download it and unzip into .claude/skills/ at your working directory. The per-package CLAUDE.md is served separately by the get_package_context tool.",
        )}
      >
        <Button size="sm" className="mt-3" disabled={downloading} onClick={getSkill}>
          {downloading ? t("Preparing…") : t("Download skill (.zip)")}
        </Button>

        <div className="mt-4">
          <div className="text-[11px] font-semibold text-muted-foreground">
            {t("Or run this in your working directory (uses your token from step 2):")}
          </div>
          <div className="mt-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            Linux / macOS
          </div>
          <CodeLine text={skillSh(tok)} />
          <div className="mt-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            Windows (PowerShell)
          </div>
          <CodeLine text={skillPs(tok)} />
        </div>
      </Step>

      <Step
        n={4}
        title={t("4 · Connect to the SAP system (ADT MCP)")}
        desc={t(
          "For ABAP work on the target SAP GROW: enable SAP's ADT MCP Server inside Eclipse and add it as a second connector, so your Claude can read, create, activate and test objects on the target.",
        )}
      >
        <ol className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-foreground">
          <li>
            {t(
              "Install Eclipse for ABAP (2026-06) with ADT and connect an ABAP Cloud Project to the dev tenant.",
            )}
          </li>
          <li>
            {t(
              "In Eclipse: Preferences → ABAP Development → MCP Server → Enable, then Apply and copy the generated token (port 2234).",
            )}
          </li>
          <li>
            {t(
              "For read tools (search / read source / where-used), add the arc1 extension (see the full guide).",
            )}
          </li>
        </ol>
        <CodeLine text={SAP_ADT_CMD} />
        <div className="mt-2 text-[11px] text-muted-foreground">
          {t("Full step-by-step:")}{" "}
          <span className="font-mono">docs/code-companion-sap-dev-setup.md</span>
        </div>
      </Step>

      <div className="rounded-xl border bg-card p-4">
        <div className="font-brand text-sm font-semibold text-primary">
          {t("How it works")}
        </div>
        <ol className="mt-3 space-y-2 text-[12px] leading-relaxed text-foreground">
          <li>
            <b>{t("Verify the connection.")}</b>{" "}
            {t(
              "Run `claude`, then `/mcp` — mygrow (and sap-adt, if you set it up) should show as connected before you start.",
            )}
          </li>
          <li>
            <b>{t("Work by chatting.")}</b>{" "}
            {t(
              "Claim a package, pull its context and legacy code, build the solution with tests, and report status in plain language — the skill maps it to the board.",
            )}
          </li>
          <li>
            <b>{t("Don't guess missing context.")}</b>{" "}
            {t(
              "If a table, field, function module or business rule isn't in the context, raise a flag (context incorrect / to verify) and leave a TODO. The owner resolves it and re-releases an updated context.",
            )}
          </li>
          <li>
            <b>{t("Don't deploy while flags are open.")}</b>{" "}
            {t("Mark the package ready only when every in-scope object is migrated and tested.")}
          </li>
        </ol>
        <div
          className="mt-3 rounded-lg p-3 text-[11px] leading-relaxed"
          style={{ background: `hsl(var(--warning) / 0.1)`, color: "hsl(var(--foreground))" }}
        >
          <b>{t("MCP not connected?")}</b>{" "}
          {t(
            "Your token may have rotated (each 'Generate' issues a new one and revokes the old). Regenerate above, then run `claude mcp remove mygrow` and add it again with the fresh command.",
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="font-brand text-sm font-semibold text-primary">
          {t("Example prompts")}
        </div>
        <div className="mt-3 space-y-2">
          {PROMPTS.map((p) => (
            <div
              key={p.title}
              className="flex items-center gap-3 rounded-lg border bg-secondary/40 p-3"
            >
              <div className="flex-1">
                <div className="text-xs font-semibold">{t(p.title)}</div>
                <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {t(p.text)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard?.writeText(p.text);
                  toast.success(t("Copied"));
                }}
              >
                {t("Copy")}
              </Button>
            </div>
          ))}
        </div>
        <div
          className="mt-4 rounded-lg p-3 text-[11px] leading-relaxed"
          style={{ background: `hsl(var(--info) / 0.08)`, color: "hsl(var(--foreground))" }}
        >
          <b>{t("Where the code goes:")}</b>{" "}
          {t(
            "into the SAP GROW target itself. You create and activate each object through the ADT MCP, and myGROW validates before (preflight) and records every phase after — no repository of migrated files in the middle.",
          )}
          <div className="mt-2">
            <b>{t("Two ways to run it:")}</b>{" "}
            {t(
              "assisted, where you drive object by object from your terminal; or autonomous, where the owner starts a run from the board and the worker does the same steps with the ADT session of whoever clicked. Both write to the same ledger.",
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
